/**
 * VAT by month, collapsible, showing what actually caused it.
 *
 * WHY (2026-08-30, Fan): "I need every month to be collapsible also showing
 * the bookings that cause that VAT. Because I am pretty sure it does not
 * consider any VAT the suppliers charge." He was right -- input VAT was
 * structurally zero. Each month now expands into the client invoices behind
 * the output VAT and the supplier documents behind the input VAT, split into
 * accommodation (COGS) and overhead, so the net figure is auditable.
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { brosTaxReservesAPI } from './../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

function num(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function MonthBlock({ month, currency, data }) {
  const [open, setOpen] = useState(false);
  const net = num(data.net_vat_due);

  return (
    <>
      <tr
        className="border-b hover:bg-muted/40 cursor-pointer"
        onClick={() => setOpen((prior) => !prior)}
        data-testid={`vat-month-${month}-${currency}`}
      >
        <td className="py-2 pl-2">
          <span className="inline-flex items-center gap-1 font-medium">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {month}
          </span>
        </td>
        <td className="py-2 font-mono text-xs">{currency}</td>
        <td className="py-2 text-right tabular-nums">{formatCurrency(num(data.output_vat), currency)}</td>
        <td className="py-2 text-right tabular-nums text-muted-foreground">
          {formatCurrency(num(data.input_vat), currency)}
        </td>
        <td className={`py-2 text-right tabular-nums font-medium ${net < 0 ? 'text-green-600' : ''}`}>
          {formatCurrency(net, currency)}
        </td>
        <td className="py-2 text-right text-xs text-muted-foreground">
          {data.invoice_count} inv &middot; {data.supplier_document_count} supplier
          {data.supplier_documents_unresolved > 0 && (
            <Badge variant="outline" className="ml-1 text-[10px] text-amber-600">
              {data.supplier_documents_unresolved} unreadable
            </Badge>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b bg-muted/20">
          <td colSpan={6} className="px-4 py-3 space-y-4">
            <div className="text-xs text-muted-foreground">
              Input VAT this month: accommodation{' '}
              <strong>{formatCurrency(num(data.input_vat_cogs), currency)}</strong>, overhead{' '}
              <strong>{formatCurrency(num(data.input_vat_overhead), currency)}</strong>. Both are
              reclaimable; only the first is a cost of a booking.
            </div>

            {data.invoices?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Client invoices paid (output VAT)
                </p>
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium py-1">Invoice</th>
                      <th className="text-left font-medium py-1">Date</th>
                      <th className="text-left font-medium py-1">Client</th>
                      <th className="text-left font-medium py-1">Property / stay</th>
                      <th className="text-right font-medium py-1">Received</th>
                      <th className="text-right font-medium py-1">Output VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((invoice) => (
                      <tr key={`${invoice.invoice_ref}-${invoice.date}`} className="border-t">
                        <td className="py-1 font-mono">{invoice.invoice_ref}</td>
                        <td className="py-1">{invoice.date}</td>
                        <td className="py-1">{invoice.client ?? '\u2014'}</td>
                        <td className="py-1 text-muted-foreground">
                          {invoice.property ?? '\u2014'}
                          {invoice.stay && <span className="ml-1">({invoice.stay})</span>}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(invoice.amount), currency)}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(invoice.output_vat), currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.supplier_documents?.length > 0 ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Supplier documents (input VAT)
                </p>
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium py-1">Document</th>
                      <th className="text-left font-medium py-1">Date</th>
                      <th className="text-left font-medium py-1">Supplier</th>
                      <th className="text-left font-medium py-1">Kind</th>
                      <th className="text-right font-medium py-1">Amount</th>
                      <th className="text-right font-medium py-1">Input VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.supplier_documents.map((doc) => (
                      <tr key={doc.document_id} className="border-t">
                        <td className="py-1 font-mono">{doc.invoice_number ?? '\u2014'}</td>
                        <td className="py-1">{doc.date ?? '\u2014'}</td>
                        <td className="py-1">{doc.supplier ?? '\u2014'}</td>
                        <td className="py-1">
                          <Badge variant="outline" className="text-[10px]">
                            {doc.kind === 'cogs' ? 'accommodation' : 'overhead'}
                          </Badge>
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(doc.amount), doc.currency ?? currency)}
                        </td>
                        <td className="py-1 text-right tabular-nums">
                          {formatCurrency(num(doc.input_vat), doc.currency ?? currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-amber-600">
                No supplier documents with VAT for this month and currency, so the
                net figure is output VAT only.
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function BROSVatMonths({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await brosTaxReservesAPI.getVatMonths(year);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive" data-testid="vat-months-error">
          {error}
        </CardContent>
      </Card>
    );
  }

  const coverage = data?.supplier_vat_coverage ?? {};
  const rows = [];
  (data?.months ?? []).forEach((month) => {
    Object.entries(month.by_currency ?? {}).forEach(([currency, currencyData]) => {
      rows.push({ month: month.month, currency, data: currencyData });
    });
  });

  return (
    <Card data-testid="bros-vat-months">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">VAT by month</CardTitle>
        <CardDescription className="text-xs">
          Output VAT on client payments received, net of the input VAT on
          supplier documents in the same period. Expand a month to see both sides.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 shrink-0" />
          Input VAT read from {coverage.documents_considered ?? 0} supplier
          documents ({coverage.matched_by_taxonomy ?? 0} classified as BR OS
          spend, {coverage.matched_by_supplier_name ?? 0} matched by supplier
          name{coverage.excluded_self_billed_count
            ? `, ${coverage.excluded_self_billed_count} excluded as self-billed`
            : ''}). Supplier invoices carry no booking reference, so input VAT
          is a period figure and is not attributed per booking.
        </p>
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="text-left font-medium py-1 pl-2">Month</th>
              <th className="text-left font-medium py-1">Currency</th>
              <th className="text-right font-medium py-1">Output VAT</th>
              <th className="text-right font-medium py-1">Input VAT</th>
              <th className="text-right font-medium py-1">Net VAT due</th>
              <th className="text-right font-medium py-1">Behind it</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <MonthBlock
                key={`${row.month}-${row.currency}`}
                month={row.month}
                currency={row.currency}
                data={row.data}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No VAT activity for {data?.year}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default BROSVatMonths;
